// The end of the battle on a player's phone: the podium card, the details step (9 Oct), the result page and its form.
// Rounds' voice: a wry registrar on a busy shift. Each fact once per screen; a line only says what the facts support
// (place, the gap to the place above, points and answers), never "so close" unless it was. Pure and unit-tested.
import type { FinishView, LeaderboardRow, MeResponse } from '@shared/types';
import { DEFAULT_FINISH } from '@shared/finish';
import { fill, ordinal, pick } from '@/lib/kudos';
import { resultSummary, seconds, type ResultSummary } from './kudosData';
import type { Stat } from './StatGrid';

export const FINISH = {
  podium: 'The podium', topThree: 'Top three', you: 'Your result', isYou: '(you)',
  review: 'Review my answers', reviewQuestions: 'Review the questions',
  loading: 'Loading your result…',
  points: 'Points', accuracy: 'Accuracy', streak: 'Best streak', fastest: 'Fastest right answer',
  // The result page.
  title: (name: string) => `${name}'s result`,
  none: 'No result to show. Join a battle first.',
  failed: 'Could not load your result. Check your connection and try again.',
  copyPage: 'Copy my result link', copied: 'Link copied',
  playNext: 'Play the next battle',
  // 14 Oct: the share chain.
  friendsJoined: (n: number) => (n === 1 ? '1 friend joined through your link.' : `${n} friends joined through your link.`),
  challengeTitle: (name: string) => `You against ${name}`,
  challengeWon: (name: string, by: number) => `You beat ${name} by ${by.toLocaleString('en-GB')} points. Send your own link and keep it going.`,
  challengeLost: (name: string, by: number) => `${name} stays ahead by ${by.toLocaleString('en-GB')} points. The next battle is another go.`,
  challengeTied: (name: string) => `Level with ${name}. Settle it in the next battle.`,
  challengeLobby: (name: string, score: number, title: string) => `Beat ${name}'s ${score.toLocaleString('en-GB')} from ${title}.`,
  // The details form. The draw box starts unticked: the prize terms say players choose to enter.
  firstName: 'First name', lastName: 'Last name', medSchool: 'Medical school', year: 'Year', choose: 'Choose…',
  // Opened from a result link on a device that did not join: the link only reads, so the form asks who is saving.
  email: 'Email you joined with', emailWhy: 'You opened this page from a link, so we need the email you joined with before saving.',
  draw: (phrase: string) => `Enter me in the draw for ${phrase}.`,
  agreeBefore: 'By entering you agree to the ', agreeLink: 'prize terms', agreeAfter: '.',
  closed: (when?: string) => (when ? `The draw closed ${when}.` : 'The draw has closed.'),
  saving: 'Saving…',
  network: 'Could not save. Check your connection and try again.',
  // 6 Oct: the end-of-battle form with the trial offer (its heading, message, button and thank-you come from the builder).
  country: 'Country', subject: 'Subject', university: 'University', prep: 'Preparing for', optional: '(optional)',
  emailNote: "We'll use the email you joined with.",
  welcome: (first: string) => `Welcome back, ${first}.`,
  history: (battles: number, best?: number) => (battles > 1 ? `Battle ${battles} for you.${best ? ` Best place so far: ${ordinal(best)}.` : ''}` : ''),
  savedLast: 'Your details are saved from last time, so there is nothing to fill in.',
  yearCheck: (year: string) => `Still in ${year}?`, yearSave: 'Update my year',
  cancel: 'Cancel', enterDraw: 'Enter the draw',
  // 9 Oct: the details step between the podium and the result, and the quiet ways past it.
  notNow: 'Not now', change: 'Change my details',
  drawIn: "You're in the draw.", drawClosed: 'The draw had closed, so this is not an entry.',
  // 8 Oct: keeping the result under an account, offered once the battle is over.
  keepTitle: 'Keep your results', keepLine: "An account keeps tonight's result, and every one after it, under your address.",
  keep: 'Create an account', myBattles: 'Your battles',
  // The review: open for everyone, never behind the form (9 Oct).
  answers: 'Your answers', question: (index: number) => `Question ${index + 1}`, mine: 'You', right: 'Correct',
  reviewLater: 'Your answers appear here when the battle finishes.',
} as const;

export interface FinishFacts extends ResultSummary {
  playerId: string;
  /** Points between you and the place above (from the final top ten, so known down to 11th). */
  gapAbove?: number;
  /** First place only: your margin over second. */
  marginBelow?: number;
}

/** The result's figures plus the gaps the line may mention, from /me and the final top ten. */
export function finishFacts(me: MeResponse, top?: readonly LeaderboardRow[]): FinishFacts {
  const sum = resultSummary(me);
  const rows = top ?? me.state?.top ?? [];
  const rank = sum.rank;
  const above = rank && rank > 1 ? rows.find((r) => r.rank === rank - 1) : undefined;
  const below = rank === 1 ? rows.find((r) => r.rank === 2) : undefined;
  return {
    ...sum, players: Math.max(sum.players, rank ?? 0), playerId: me.playerId,
    gapAbove: above ? Math.max(0, above.score - sum.score) : undefined,
    marginBelow: below ? Math.max(0, sum.score - below.score) : undefined,
  };
}

export const LINES = {
  none: 'No answers, no points. Next time, even a guess can score.',
  zero: 'Every miss here is one fewer in finals.',
  solo: 'Unopposed. Bring a rival next time.',
  winner: ['Ward round champion.', 'The consultant wants a word. A nice one.'],
  tieWin: 'Same points as 2nd. Quicker answers won it.',
  closeWin: 'Won by {gap}. Not a heartbeat to spare.',
  tied: 'Same points as {place}. Quicker answers took it.',
  close: '{gap} off {target}. So close you could hear the bleep.',
  oneMore: '{gap} off {target}. One more right answer would have done it.',
  podium: 'One for the portfolio.',
  blanks: 'Every answer right. The blanks cost you.',
  elite: 'Ahead of {pct}% of the room. Consultant material.',
  upper: 'Ahead of {pct}% of the room.',
  rest: ['Every miss here is one fewer in finals.', 'The next battle is yours.'],
} as const;

/** A gap this small is a photo finish; one right answer (500 points or more) closes anything under ONE_ANSWER. */
export const CLOSE_GAP = 100;
export const ONE_ANSWER = 500;

const pts = (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'point' : 'points'}`;
/** What the place above is called: the podium from 4th, the top ten from 11th, otherwise its ordinal. */
const targetAbove = (rank: number) => (rank === 4 ? 'the podium' : rank === 11 ? 'the top ten' : ordinal(rank - 1));

/**
 * The one line on the podium card and the share card. It never repeats the place or a figure shown beside it; it adds
 * the gap to the place above when that is the story, and otherwise reacts to the facts. Deterministic per player.
 */
export function finishLine(f: FinishFacts): string | undefined {
  if (f.answered === 0) return LINES.none;
  if (f.score === 0) return LINES.zero;
  const rank = f.rank;
  if (!rank) return undefined;
  const seed = `${f.playerId}:finish`;
  if (rank === 1) {
    if (f.players <= 1) return LINES.solo;
    if (f.marginBelow === 0) return LINES.tieWin;
    if (f.marginBelow !== undefined && f.marginBelow <= CLOSE_GAP) return fill(LINES.closeWin, { gap: pts(f.marginBelow) });
    return pick(LINES.winner, seed);
  }
  if (f.gapAbove !== undefined) {
    if (f.gapAbove === 0) return fill(LINES.tied, { place: ordinal(rank - 1) });
    if (f.gapAbove <= CLOSE_GAP) return fill(LINES.close, { gap: pts(f.gapAbove), target: targetAbove(rank) });
    if (f.gapAbove < ONE_ANSWER) return fill(LINES.oneMore, { gap: pts(f.gapAbove), target: targetAbove(rank) });
  }
  const last = rank >= f.players;
  if (rank <= 3 && !last) return LINES.podium;
  if (f.correct === f.answered && f.answered < f.asked) return LINES.blanks;
  const pct = Math.floor((100 * (f.players - rank)) / f.players);
  if (pct >= 90) return fill(LINES.elite, { pct });
  if (pct >= 50) return fill(LINES.upper, { pct });
  return pick(LINES.rest, seed);
}

/**
 * The figures as tiles, leaving out any that would be empty (no fastest right answer, a best streak of 0). Points can be
 * left out where the screen already shows them (your step on the podium). No answers or no points: no tiles at all, the
 * line says it instead.
 */
export function finishStats(f: ResultSummary, opts: { points?: boolean } = {}): Stat[] {
  if (f.answered === 0 || f.score === 0) return [];
  const out: Stat[] = [];
  if (opts.points !== false) out.push({ label: FINISH.points, value: f.score.toLocaleString('en-GB') });
  if (f.accuracy !== undefined) out.push({ label: FINISH.accuracy, value: `${f.accuracy}%` });
  if (f.bestStreak > 0) out.push({ label: FINISH.streak, value: String(f.bestStreak) });
  if (f.fastestMs !== undefined) out.push({ label: FINISH.fastest, value: `${seconds(f.fastestMs)} s` });
  return out;
}

/** The battle's form and its offer as /me gives it (always, since 9 Oct; DEFAULT_FINISH stands in for a reply without one). */
export const finishOf = (me: Pick<MeResponse, 'finish'>): FinishView => me.finish ?? DEFAULT_FINISH;

/**
 * 9 Oct: whether the details step between the podium and the result asks this player: when the battle collects details
 * and none have been given for this seat (profileDone). Details confirmed from a proved seat are copied into the seat
 * when it is proved (markVerified), so a signed-in returning player is profileDone and passes straight through. Details
 * on /me without profileDone can only be a prefill (saved on an earlier, unproved visit), which the proved owner
 * confirms on the form: asked, with the form filled (Details withSaved). An address that is known but not proved is
 * asked too, with an empty form: what it saved before never reaches a stranger.
 */
export function asksForDetails(me: Pick<MeResponse, 'finish' | 'profileDone'>): boolean {
  return finishOf(me).askDetails && !me.profileDone;
}

/** What the result page says about the details: nothing yet ('no'), saved before this visit, saved here, entered in the draw, or saved after the draw closed. */
export type SavedKind = 'no' | 'before' | 'plain' | 'draw' | 'closed';
/** What the details step hands the result page when it goes there after a save (router state): the draw note, if any, comes with the thank-you. */
export interface ResultArrival { saved?: Exclude<SavedKind, 'no' | 'before'> }
